/** @format */

import {
	Component,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { DeviceService, Logger, Scene7Service, WINDOW } from '@shared/data-access/services';
import { HeaderStoreSelection, ProductSku } from '@shared/data-access/interfaces';
import {
	ReactiveFormsModule,
	UntypedFormControl,
	UntypedFormGroup,
	Validators
} from '@angular/forms';

import { DefiningAttributeName } from '@pdp/data-access/enums';
import { Store as NGRXStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductService } from '@shared/data-access/product';
import { selectKillswitch } from '@pdp/data-access/state';
import { CommonModule } from '@angular/common';
import { ProductAvailabilityNotificationGoldMessageComponent } from '@pdp/exclusive-products/components';
import { ProductRecommendationsComponent } from '@shared/components';
import {
	HmfAlertBannerComponent,
	HmfButtonComponent,
	HmfTextInputComponent
} from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	homefieldIconSignCircledCheck,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';

@Component({
	selector: 'pdp-product-availability-notification-form',
	templateUrl: './product-availability-notification-form.component.html',
	styleUrl: './product-availability-notification-form.component.scss',
	imports: [
		CommonModule,
		HmfAlertBannerComponent,
		HmfButtonComponent,
		HmfTextInputComponent,
		HomefieldIconLibraryModule,
		ProductAvailabilityNotificationGoldMessageComponent,
		ProductRecommendationsComponent,
		ReactiveFormsModule
	]
})
export class ProductAvailabilityNotificationFormComponent implements OnInit, OnChanges {
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconSignCircledCheck]);
	}

	/**
	 * @description Element references needed for analytics
	 */
	@ViewChild('atsNotificationCheckbox', { static: false }) atsNotificationCheckBoxRef: ElementRef;
	@ViewChild('bopisNotificationCheckbox', { static: false })
	bopisNotificationCheckBoxRef: ElementRef;

	/**
	 * @description sku - used for analytics, product information and in stock reminder API call
	 */
	@Input() sku: ProductSku = null;

	/**
	 * @description productTitle - Displayed in template
	 */
	@Input() productTitle = '';

	/**
	 * @description productECode - Used in analytics and in stock reminder API call
	 */
	@Input() productECode = '';

	/**
	 * @description storeDetails - Used in stock reminder API call
	 */
	@Input() storeDetails?: HeaderStoreSelection = null;

	/**
	 * @description current selected shipping mode
	 */
	@Input() shipMode: string;

	/**
	 * @description panelClosed - Triggered when 'Cancel' button is clicked, prompting parent component to close side panel
	 */
	@Output() panelClosed: EventEmitter<void> = new EventEmitter<void>();

	private readonly scene7Service = inject(Scene7Service);
	private readonly analyticsService = inject(ProductAnalyticsService);
	private readonly productService = inject(ProductService);
	private readonly logger = inject(Logger);
	protected window = inject(WINDOW) as Window;
	private readonly deviceService = inject(DeviceService);
	private readonly ngrxStore = inject(NGRXStore);

	/**
	 * @description notifyUserForm - Form used to collect user's email and notification preference
	 */
	public notifyUserForm: UntypedFormGroup;

	/**
	 * @description submitAttempted - Using this to show form requirement errors on off chance that a user clicks the submit button when the form is invalid
	 */
	public submitAttempted: boolean;

	/**
	 * @description showSuccessMsg - Determine whether we should show the success message
	 */
	public showSuccessMsg: boolean;

	/**
	 * @description showErrorMsg - Determine whether we should show the error message
	 */
	public showErrorMsg: boolean;

	/**
	 * @description attributeDisplay - String containing the products attributes, is displayed in the template
	 */
	public attributeDisplay: string;

	/**
	 * @description labelValuesAttributes - Array of objects containing key value pairs label and value, used for in stock reminder API call
	 */
	private labelValuesAttributes: { label: string; value: string }[];

	/**
	 * @description colorValue - String contaiing the skus color attribute value, used to retrieve product image, which is then used for in stock reminder API call
	 */
	private colorValue = '';

	/**
	 * @description cancelButtonText - Will change depending if the user successfully submits their email and notification preference
	 */
	public cancelButtonText = 'Cancel';

	private analyticEventData: {
		atsNotification?: HTMLInputElement;
		ecode: string;
		shipMode?: string;
		sku: ProductSku;
		store: string;
	};
	public notifyMeGoldMessageEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('notifyMeSCGoldMessageEnabled')
	);
	public isLoadingStateRecsEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isLoadingStateRecsEnabled')
	);
	protected isNewRecsCardsNotifyMePanelEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isNewRecsCardsNotifyMePanelEnabled')
	);

	ngOnInit() {
		this.notifyUserForm = new UntypedFormGroup({
			email: new UntypedFormControl('', [Validators.required, Validators.email])
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		// If we have any sku change then we need to update attributeDisplay, labelValuesAttributes and colorValue
		if (changes.sku) {
			this.runThroughProductAttributes();
		}
		if (!this.notifyUserForm) {
			return;
		}
	}

	get notifyUserFormControls() {
		return this.notifyUserForm.controls;
	}

	public closePanel() {
		this.panelClosed.emit();
	}
	/**
	 * @description Makes API call to set up a in stock reminder for the user
	 */
	public notifyUser(shipMode: string) {
		this.submitAttempted = true;
		this.showSuccessMsg = false;
		this.showErrorMsg = false;

		if (this.notifyUserForm.invalid) {
			return;
		}
		this.analyticEventData = {
			ecode: this.productECode,
			sku: this.sku,
			store: this.storeDetails?.store || this.storeDetails?.location || ''
		};

		const imageUrl = `${this.scene7Service.getProductImageUrl(
			this.productECode,
			'ProductBean',
			this.colorValue,
			undefined,
			{},
			false
		)}?qlt=70&wid=600&fmt=jpeg`;

		let ref = window.location.href;
		if (ref.includes('m2.dickssportinggoods')) {
			ref = ref.replace('m2.dickssportinggoods', 'www.dickssportinggoods');
		}

		const tempRes = {
			email: this.notifyUserFormControls.email.value,
			sku: this.sku?.partNumber,
			title: this.productTitle,
			productURL: `${ref.split('?')[0]}?sku=${this.sku?.partNumber}`,
			imageURL: imageUrl,
			attributes: this.labelValuesAttributes
		};

		if (shipMode === 'shipToMe') {
			this.analyticEventData['shipMode'] = this.shipMode;
			const { email, sku, title, productURL, imageURL, attributes } = tempRes;
			this.productService
				.setProductInStockReminder(
					email,
					sku,
					title,
					productURL,
					imageURL,
					attributes,
					'0',
					null,
					1
				)
				.subscribe({
					next: this.inStockReminderSuccess.bind(this),
					error: this.inStockReminderError.bind(this)
				});
		}

		if (shipMode === 'bopis') {
			this.analyticEventData['shipMode'] = this.shipMode;
			const { email, sku, title, productURL, imageURL, attributes } = tempRes;

			// logging the store number undefined issue
			if (!this.isValidRequest(this.storeDetails)) {
				const notifyAlert = {
					message: 'store_undefined_issue',
					sku,
					productURL,
					storeNumber: this.storeDetails?.store || this.storeDetails?.location,
					storeName: this.storeDetails?.name,
					zip: this.storeDetails?.zipcode || this.storeDetails?.zip,
					browser: this.deviceService.browser,
					browserVersion: this.deviceService.browser_version,
					isMobile: this.deviceService.isMobile(),
					os: this.deviceService.os,
					osVersion: this.deviceService.os_version
				};
				this.logger.log(notifyAlert);
			}

			this.productService
				.setProductInStockReminder(
					email,
					sku,
					title,
					productURL,
					imageURL,
					attributes,
					this.storeDetails?.store || this.storeDetails?.location,
					this.storeDetails?.name,
					1
				)
				.subscribe({
					next: this.inStockReminderSuccess.bind(this),
					error: this.inStockReminderError.bind(this)
				});
		}
	}

	public isValidRequest(storeDetails?: HeaderStoreSelection) {
		if (!storeDetails) {
			return false;
		}
		return storeDetails.location !== undefined || storeDetails.store !== undefined;
	}

	/**
	 * @description Loops through our sku's defAttributes (if there are any) and sets the values for attributeDisplay, labelValuesAttributes and colorValue
	 */
	public runThroughProductAttributes(): void {
		this.attributeDisplay = '';
		this.labelValuesAttributes = [];

		let defAttr = [];

		if (this.sku?.definingAttributes && this.sku?.definingAttributes.length > 0) {
			defAttr = this.sku?.definingAttributes;
		} else {
			defAttr = [];
		}
		if (defAttr && defAttr.length > 0) {
			for (let i = 0; i < defAttr.length; ++i) {
				const attr = defAttr[i];
				this.attributeDisplay += `${attr.name}: ${attr.value}${
					i < defAttr.length - 1 ? ', ' : ''
				}`;
				const attrLabelValue = {
					label: attr.name,
					value: attr.value
				};
				this.labelValuesAttributes.push(attrLabelValue);
				if (
					typeof attr.name === 'string' &&
					attr.name.trim().toLowerCase() === DefiningAttributeName.COLOR
				) {
					this.colorValue = attr.value.replace(/[/ -]/g, '_');
				}
			}
		}
	}
	/**
	 * @description
	 * Show success message after in stock reminder is set
	 * Hide success message after five seconds
	 */
	private inStockReminderSuccess() {
		this.analyticsService.triggerAnalyticsEvent('NotifyMeStock', this.analyticEventData);
		this.showSuccessMsg = true;
		this.cancelButtonText = 'Close';
	}
	/**
	 * @description
	 * Show error message after in stock reminder is set
	 * Hide error message after five seconds
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private inStockReminderError(err?: Error) {
		this.showErrorMsg = true;
		this.cancelButtonText = 'Cancel';
		setTimeout(() => {
			this.showErrorMsg = false;
		}, 5000);
	}

	get emailValidationState(): 'missing' | 'invalid' | false {
		if (
			(this.notifyUserFormControls.email.touched || this.submitAttempted) &&
			this.notifyUserFormControls.email.errors?.required
		) {
			return 'missing';
		} else if (
			(this.submitAttempted && this.notifyUserFormControls.email.invalid) ||
			(this.notifyUserFormControls.email.touched &&
				this.notifyUserFormControls.email.errors?.email)
		) {
			return 'invalid';
		}

		return false;
	}
}
